/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useMemo} from 'react';
import {useQuery, UseQueryResult} from 'react-query';
import {flattenAccountTree} from '@ion/core/src/entities/Accounts';
import {error} from '@ion/core/src/logger/logger';
import {getSubAccounts} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {Account, AccountLink} from '@ion/core/src/types/entities';
import {sortByString} from '@ion/core/src/utils/ArrayUtil';
import {createKey, QueryKey} from '../config/QueryKeys';
import {createQueryResult, getStaleTime} from '../utils/helpers';

/**
 * The UseGetSubAccountsProps interface defines the set of properties
 * that can be passed as parameters to the useGetSubAccounts hook.
 */
export interface UseGetSubAccountsProps {
  account: AccountLink;
  enabled?: boolean;
  includeParent?: boolean;
};

/**
 * The UseGetSubAccoutResults type represents the results
 * returned by the useGetSubAccounts hook.
 */
export type UseGetSubAccoutResults = {
  subAccounts: (AccountLink | Account)[];
  refresh: Function;
} & UseQueryResult;

/**
 * The useGetSubAccounts hook is designed to fetch and manage the list of sub-accounts
 * for a given account. It provides a convenient way to retrieve and refresh the sub-accounts data.
 *
 * ```typescript
 *
 *  import {useGetSubAccounts} from '@ion/query/src/hooks/useGetSubAccounts';
 *
 *  const subAccounts = useGetSubAccounts({account});
 *
 * ```
 */
export const useGetSubAccounts = ({
  account,
  enabled = true,
  includeParent = false
}: UseGetSubAccountsProps) => {
  const accountId = useMemo(() => entityId(account), [account]);

  const query = useQuery(
    createKey(QueryKey.GET_SUB_ACCOUNTS, accountId),
    async() => {
      try {
        const subAccounts = await getSubAccounts(accountId, true);
        const allChildren = flattenAccountTree(subAccounts);
        const results = includeParent ? [account, ...allChildren] : allChildren;
        return sortByString(results, 'title');
      } catch (e) {
        error(e.message);
        return [];
      }
    },
    {enabled}
  );

  const refresh = useCallback(() => {
    query.refetch();
  }, [query]);

  return createQueryResult(query, {
    subAccounts : query.data,
    refresh
  }) as UseGetSubAccoutResults;
};

export default useGetSubAccounts;
